import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getSortedPosts } from '@/data/posts'
import SEO from '@/components/SEO'

const ALL_POSTS = getSortedPosts()

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
        year:  'numeric',
        month: 'short',
        day:   'numeric',
    })
}

const TAG_COLORS: Record<string, string> = {
    community:   '#00FF88',
    stem:        '#00FF88',
    outreach:    '#00FF88',
    education:   '#00D9FF',
    mentorship:  '#00D9FF',
    python:      '#FFB300',
    'open-source': '#FFB300',
    cli:         '#FFB300',
    automation:  '#FFB300',
    pypi:        '#FFB300',
    backend:     '#C084FC',
    api:         '#C084FC',
    fastapi:     '#C084FC',
    engineering: '#C084FC',
    typescript:  '#3178C6',
    'vs-code':   '#007ACC',
    arduino:     '#00979D',
    extensions:  '#00979D',
}

function tagColor(tag: string) {
    return TAG_COLORS[tag] ?? '#555555'
}

export default function BlogPage() {
    const allTags = useMemo(() => {
        const set = new Set<string>()
        ALL_POSTS.forEach(p => p.tags.forEach(t => set.add(t)))
        return Array.from(set).sort()
    }, [])

    const [activeTag, setActiveTag] = useState<string | null>(null)

    const posts = activeTag
        ? ALL_POSTS.filter(p => p.tags.includes(activeTag))
        : ALL_POSTS

    return (
        <div className="min-h-screen font-mono py-12">
            <SEO
                title="Blog | Rapheal Kwabena Asomani Gadoh — Articles & Engineering Notes"
                description="Articles, tutorials, and engineering notes on Python workflow automation, API design, Arduino IDE tools, and STEM education outreach."
                canonical="https://blog.raphealgadoh.me/"
            />
            <div className="max-w-4xl mx-auto px-6 space-y-10">

                {/* ── Page header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <h1 className="text-sm mb-1">
                        <span className="text-primary">rapheal@portfolio</span>
                        <span className="text-[#444]">:~$</span>
                        <span className="text-foreground ml-2">ls blog/</span>
                    </h1>
                    <p className="text-xs text-[#555]">
                        // {ALL_POSTS.length} entries — engineering, community, open source.
                    </p>
                </motion.div>

                {/* ── Tag filter ── */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 }}
                    className="flex flex-wrap gap-2"
                >
                    <button
                        onClick={() => setActiveTag(null)}
                        className={[
                            'text-[10px] px-2.5 py-1 border rounded-sm font-mono transition-all duration-150',
                            activeTag === null
                                ? 'border-primary/50 text-primary bg-primary/5'
                                : 'border-[#1a1a1a] text-[#555] hover:text-foreground hover:border-[#333]',
                        ].join(' ')}
                    >
                        all
                    </button>
                    {allTags.map(tag => (
                        <button
                            key={tag}
                            onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                            className={[
                                'text-[10px] px-2.5 py-1 border rounded-sm font-mono transition-all duration-150',
                                activeTag === tag
                                    ? 'border-primary/50 text-primary bg-primary/5'
                                    : 'border-[#1a1a1a] text-[#555] hover:text-foreground hover:border-[#333]',
                            ].join(' ')}
                        >
                            #{tag}
                        </button>
                    ))}
                </motion.div>

                {/* ── Post cards ── */}
                <div className="space-y-0">
                    {posts.map((post, i) => (
                        <motion.div
                            key={post.slug}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * i, duration: 0.35 }}
                        >
                            <Link
                                to={`/blog/${post.slug}`}
                                className="block border-b border-[#111] py-6 px-3 -mx-3 rounded-sm
                                           hover:bg-[#0a0a0a] transition-colors duration-150 group"
                            >
                                {/* Date + reading time */}
                                <div className="flex items-center gap-3 text-[10px] text-[#444] mb-2">
                                    <span>{formatDate(post.date)}</span>
                                    <span className="text-[#2a2a2a]">—</span>
                                    <span>{post.readingTime}</span>
                                </div>

                                {/* Title */}
                                <h2 className="text-sm text-foreground group-hover:text-primary
                                               transition-colors duration-150 mb-2 leading-snug">
                                    <span className="text-primary mr-2">&gt;</span>
                                    {post.title}
                                </h2>

                                {/* Excerpt */}
                                <p className="text-xs text-[#555] leading-relaxed mb-3">
                                    {post.excerpt}
                                </p>

                                {/* Tags */}
                                <div className="flex flex-wrap gap-2">
                                    {post.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="text-[9px] font-mono px-1.5 py-0.5 border rounded-sm"
                                            style={{
                                                color:       tagColor(tag),
                                                borderColor: `${tagColor(tag)}30`,
                                                background:  `${tagColor(tag)}08`,
                                            }}
                                        >
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                {posts.length === 0 && (
                    <p className="text-xs text-[#444] font-mono">
                        // No posts match the selected tag.
                    </p>
                )}

            </div>
        </div>
    )
}
